import React from 'react';

import {
  ClientContextType,
  ClientProviderType,
  ProviderStateType,
  TokenStoreType,
} from './types';

export const defaultClientContext: ClientContextType = {
  status: 'anonymous',
  hasError: false,
  login: () => {},
  logout: () => {},
  get: () => () => {},
  getPromise: () => {},
  post: () => () => {},
  postPromise: () => {},
  postJson: () => Promise.reject(),
  put: () => () => {},
  putPromise: () => {},
  patch: () => () => {},
  patchPromise: () => {},
  deletion: () => () => {},
  upload: () => {},
  getToken: () => {
    return {
      accessToken: 'string',
      refreshToken: 'string',
      expiredAt: new Date(),
    };
  },
};

export const ClientContext =
  React.createContext<ClientContextType>(defaultClientContext);

export const makeClientProvider =
  (tokenStore: TokenStoreType): React.FC<ClientProviderType> =>
  ({ client, children }) => {
    const [status, setStatus] = React.useState<ProviderStateType>({
      status: 'loading',
    });

    const api: Omit<ClientContextType, 'status'> = React.useMemo(() => {
      const login = async (email: string, password: string, otp?: string) => {
        try {
          setStatus({ status: 'authenticating' });
          // Le SDK n'envoie que l'email et le mot de passe : avec un code de double
          // authentification, la demande de jeton est construite ici (mêmes champs + otp).
          const token = otp
            ? await client.auth._requestToken({
                client_id: client._clientId,
                client_secret: client._clientSecret,
                grant_type: 'password',
                username: email,
                password,
                otp,
              })
            : await client.auth.userAuthentication({
                email,
                password,
              });

          tokenStore.set(token);
          setStatus({ status: 'authenticated' });
        } catch (err) {
          setStatus({ status: 'anonymous', hasError: true });
          console.warn(err);
        }
      };

      const logout = () => {
        tokenStore.clear();
        client.auth.logout();
        setStatus({ status: 'anonymous', hasError: false });
      };

      const get = (
        path: string,
        successCallback: (ret: any) => void,
        failureCallback?: () => void,
        options?: {
          toBlob?: boolean;
        }
      ) => {
        let active = true;
        client
          .request(path, {
            method: 'GET',
          })
          .then(payload => {
            if (active && payload.status >= 200 && payload.status <= 206) {
              return options?.toBlob ? payload.blob() : payload.json();
            } else {
              throw options?.toBlob ? payload.blob() : payload.json();
            }
          })
          .then(ret => {
            if (active && ret)
              ret.errors
                ? failureCallback && failureCallback()
                : successCallback(ret);
          })
          .catch(() => failureCallback && failureCallback());
        return () => {
          active = false;
        };
      };

      const getPromise = (path: string) =>
        client.request(path, { method: 'GET' }).then(response => {
          if (response.status >= 200 && response.status < 400)
            return response.json();

          throw response;
        });

      const post = (
        path: string,
        data: any,
        successCallback: ((createdId: string) => void) | (() => void),
        failureCallback?: () => void
      ) => {
        let active = true;
        client.request(path, { method: 'POST', body: data }).then(res => {
          if (
            active &&
            (res.status === 201 || res.status === 204 || res.status === 202)
          ) {
            /* Get ID of the created resource if it exists */
            const createdId = res.headers.get('Location')?.split('/')?.pop()!;
            return successCallback(createdId);
          } else {
            return failureCallback && failureCallback();
          }
        });
        return () => {
          active = false;
        };
      };

      const postPromise = (path: string, data: any) =>
        client.request(path, { method: 'POST', body: data }).then(response => {
          if (response.status >= 200 && response.status < 400)
            return response.headers.get('Location')?.split('/')?.pop()!;

          throw response;
        });

      // POST dont on lit la réponse (postPromise ne renvoie que l'identifiant créé)
      const postJson = (path: string, data: any) =>
        client.request(path, { method: 'POST', body: data }).then(response => {
          if (response.status >= 200 && response.status < 400)
            return response.json();

          throw response;
        });

      const put = (
        path: string,
        data: any,
        successCallback: () => void,
        failureCallback?: () => void
      ) => {
        let active = true;
        client
          .request(path, { method: 'PUT', body: data })
          .then(res =>
            active && (res.status === 202 || res.status === 201)
              ? successCallback()
              : failureCallback && failureCallback()
          )
          .catch(() => failureCallback && failureCallback());
        return () => {
          active = false;
        };
      };

      const putPromise = (path: string, data: any) =>
        client.request(path, { method: 'PUT', body: data }).then(response => {
          if (response.status >= 200 && response.status < 400)
            return response.headers.get('Location')?.split('/')?.pop()!;

          throw response;
        });

      const patch = (
        path: string,
        data: any,
        successCallback: () => void,
        failureCallback?: () => void
      ) => {
        let active = true;
        client.request(path, { method: 'PATCH', body: data }).then(res => {
          return active && res.status === 202
            ? successCallback()
            : failureCallback && failureCallback();
        });
        return () => {
          active = false;
        };
      };

      const patchPromise = (path: string, data: any) =>
        client.request(path, { method: 'PATCH', body: data }).then(response => {
          if (response.status >= 200 && response.status < 400)
            return response.headers.get('Location')?.split('/')?.pop()!;

          throw response;
        });

      const deletion = (
        path: string,
        data: any,
        successCallback: () => void,
        failureCallback?: () => void
      ) => {
        let active = true;
        client
          .request(path, { method: 'DELETE' })
          .then(res => {
            if (res.status === 202 || res.status === 204) {
              return null;
            }
            return res.json();
          })
          .then(ret => {
            if (active) {
              if (ret && ret.errors) failureCallback && failureCallback();
              else successCallback();
            }
          });
        return () => {
          active = false;
        };
      };

      const upload = (
        body: any, // Any instead of Blob so that we can access the file name
        successCallback: (mediaId: any) => void,
        failureCallback?: () => void
      ) => {
        post(
          '/medias',
          body,
          (mediaId: any) => successCallback(mediaId),
          failureCallback
        );
      };

      const getToken = () => {
        return client.auth.getToken();
      };

      return {
        getToken,
        login,
        logout,
        get,
        getPromise,
        post,
        postPromise,
        postJson,
        put,
        putPromise,
        patch,
        patchPromise,
        deletion,
        upload,
      };
    }, [client]);

    const logout = () => {
      tokenStore.clear();
      client.auth.logout();
      setStatus({ status: 'anonymous', hasError: false });
    };

    React.useEffect(() => {
      const token = tokenStore.get();

      if (client.auth.getToken()) {
        setStatus({ status: 'authenticated' });
      }

      if (!client.auth.getToken() && token) {
        const _token = {
          accessToken: token.accessToken,
          expiredAt: new Date(token.expiredAt),
          refreshToken: token.refreshToken,
        };
        client.auth.setToken(_token);
        setStatus({ status: 'authenticated' });
      }

      if (!client.auth.getToken() && !token) {
        logout();
      }

      client.event.addListener('post_auth', () => {
        const token = client.auth.getToken();
        tokenStore.set(token);
      });

      client.event.addListener('fail_auth', () => {
        logout();
      });
      // eslint-disable-next-line
    }, []);

    return (
      <ClientContext.Provider value={{ ...status, ...api }}>
        {children}
      </ClientContext.Provider>
    );
  };

const AUTH_STORAGE_KEY = 'auth';

const tokenStore: TokenStoreType = {
  get: () => {
    const token = localStorage.getItem(AUTH_STORAGE_KEY);

    if (!token) return null;

    try {
      return JSON.parse(token);
    } catch (e) {
      return null;
    }
  },
  set: token => localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(token)),
  clear: () => localStorage.removeItem(AUTH_STORAGE_KEY),
};

export const ClientProvider = makeClientProvider(tokenStore);
ClientProvider.displayName = 'ClientProvider';

export const useClient = () => React.useContext(ClientContext);
